import {
  ComponentVariant,
  type BaseProps,
} from 'src/modules/shared/types/component.type';

/**
 * Props for the CardSkeleton component
 * @interface CardSkeletonProps
 * @extends BaseProps
 */
interface CardSkeletonProps extends BaseProps {
  /**
   * Configuration for the card header skeleton
   */
  header?: {
    /** Whether to show an avatar placeholder */
    avatar?: boolean;
    /** Whether to show a title placeholder */
    title?: boolean;
    /** Whether to show a subtitle placeholder */
    subtitle?: boolean;
    /** Whether to show an action button placeholder */
    action?: boolean;
  };
  /**
   * Configuration for the card content skeleton
   */
  content?: {
    /** Number of content lines to display */
    lines?: number;
    /** Array of width classes for each line (e.g., 'w-full', 'w-3/4') */
    widths?: string[];
    /** Array of height classes for each line (e.g., 'h-4', 'h-6') */
    heights?: string[];
  };
}

export const CardSkeleton: React.FC<CardSkeletonProps> = ({
  className = '',
  header,
  content,
  variant = 'primary',
}) => {
  const variantClasses: Record<ComponentVariant, string> = {
    primary: 'bg-gray-100',
    secondary: 'bg-neutral-100',
    danger: 'bg-danger-100',
    neutral: 'bg-gray-100',
    warning: 'bg-warning-100',
    success: 'bg-success-100',
    info: 'bg-info-100',
    whatsapp: 'bg-whatsapp-100',
    call: 'bg-call-100',
    email: 'bg-email-100',
  };

  const bgClass = variantClasses[variant];

  return (
    <div
      className={`bg-white rounded-lg p-4 space-y-3 animate-pulse ${className}`}
    >
      {header && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {header.avatar && (
              <div className={`w-10 h-10 ${bgClass} rounded-full`} />
            )}
            <div className="space-y-2">
              {header.title && (
                <div className={`h-4 w-32 ${bgClass} rounded`} />
              )}
              {header.subtitle && (
                <div className={`h-3 w-24 ${bgClass} rounded`} />
              )}
            </div>
          </div>
          {header.action && <div className={`h-6 w-6 ${bgClass} rounded`} />}
        </div>
      )}
      {content && (
        <div className="space-y-2">
          {[...Array(content.lines || 2)].map((_, index) => (
            <div
              key={index}
              className={`${content.heights?.[index] || 'h-4'} ${
                content.widths?.[index] || 'w-3/4'
              } ${bgClass} rounded`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
